import {
  AnchorButton,
  Callout,
  Classes,
  Divider,
  FormGroup,
  H4,
  H5,
  H6,
  Icon,
  InputGroup,
  Intent,
  Spinner,
  SpinnerSize,
  Tab,
  Tabs,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';

import { AdvancedOptions } from 'src/components/AdvancedOptions';
import { MAX_CSV_VISIBLE_LINES } from 'src/constants/limits';
import {
  TEST_IDS,
  getProjectFileUploadPanelPreviewTabTestId,
} from 'src/constants/testIds';
import { ProjectFileUploadOptionsForm } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadOptionsForm';
import { ProjectFileUploadTable } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadTable';
import { ProjectFileUploadPanelFile } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/types';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { ExtendedParseError } from 'src/utils/csv/parseCsv';

export type ProjectFileUploadPanelProps = {
  closeTab: (closeIndex: number) => void;
  fields: ProjectFileUploadField[];
  parserErrorsByFileIdx: Record<number, ExtendedParseError[]>;
  files: ProjectFileUploadPanelFile[];
  fileOptions: ProjectFileUploadOptions;
  isParsing: boolean;
  isThereDuplicateHeaders: boolean;
  rowsByFileIdx: Record<number, string[][]>;
  selectedTab: number;
  setFields: (value: string, label: string) => void;
  setFileName: (fileName: string) => void;
  setFileOptions: (fileOptions: ProjectFileUploadOptions) => void;
  setSelectedTab: (tab: number) => void;
};

export const ProjectFileUploadPanel: React.VFC<ProjectFileUploadPanelProps> = ({
  closeTab,
  fields,
  files,
  fileOptions,
  isParsing,
  isThereDuplicateHeaders,
  parserErrorsByFileIdx,
  rowsByFileIdx,
  selectedTab,
  setFields,
  setFileName,
  setFileOptions,
  setSelectedTab,
}) => {
  const currentFile: ProjectFileUploadPanelFile | undefined =
    files[selectedTab];
  let preview: JSX.Element | undefined;
  let visibleRows = [];
  const closeTabButton =
    files.length > 1 ? (
      <Tooltip
        content={
          copy.components.projectResourceDialog.projectFileUploadPanel
            .closeTabButton
        }
      >
        <AnchorButton
          icon={IconNames.TRASH}
          minimal
          onClick={(e) => {
            e.stopPropagation();
            closeTab(selectedTab);
          }}
          outlined
        />
      </Tooltip>
    ) : null;
  if (isParsing) {
    preview = <Spinner size={SpinnerSize.LARGE} />;
  } else if (!fileOptions.separator) {
    preview = (
      <Callout
        className={classNames({
          'sd-project-file-upload-panel__preview__error-with-close':
            files.length > 1,
        })}
        intent={Intent.DANGER}
      >
        <H4>
          <Text>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel
                .validation.separator
            }
          </Text>
          {closeTabButton}
        </H4>
      </Callout>
    );
  } else if (!fileOptions.quote) {
    preview = (
      <Callout
        className={classNames({
          'sd-project-file-upload-panel__preview__error-with-close':
            files.length > 1,
        })}
        intent={Intent.DANGER}
      >
        <H4>
          <Text>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel
                .validation.quote
            }
          </Text>
          {closeTabButton}
        </H4>
      </Callout>
    );
  } else if (!fileOptions.escape) {
    preview = (
      <Callout
        className={classNames({
          'sd-project-file-upload-panel__preview__error-with-close':
            files.length > 1,
        })}
        intent={Intent.DANGER}
      >
        <H4>
          <Text>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel
                .validation.escape
            }
          </Text>
          {closeTabButton}
        </H4>
      </Callout>
    );
  } else if (parserErrorsByFileIdx[selectedTab]?.length) {
    preview = (
      <Callout
        className={classNames({
          'sd-project-file-upload-panel__preview__error-with-close':
            files.length > 1,
        })}
        intent={Intent.DANGER}
      >
        <H4>
          <Text>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel
                .errors.title
            }
          </Text>
          {closeTabButton}
        </H4>
        <AdvancedOptions
          text={() =>
            copy.components.projectResourceDialog.projectFileUploadPanel.errors
              .details
          }
        >
          {parserErrorsByFileIdx[selectedTab]?.map((error, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={idx}>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .errors.row
              }{' '}
              {error.row} - {error.message}
            </div>
          ))}
        </AdvancedOptions>
      </Callout>
    );
  } else if (rowsByFileIdx[selectedTab]?.length) {
    visibleRows = rowsByFileIdx[selectedTab].slice(0, MAX_CSV_VISIBLE_LINES);
    preview = (
      <>
        <div
          className={classNames({
            'sd-project-file-upload-panel__preview__form-with-close':
              files.length > 1,
          })}
        >
          <FormGroup
            inline
            label={
              <H6>
                {
                  copy.components.projectResourceDialog.projectFileUploadPanel
                    .form.path
                }
              </H6>
            }
            labelFor="sd-project-file-upload-panel-path"
          >
            <InputGroup
              disabled
              fill
              id="sd-project-file-upload-panel-path"
              name="path"
              value={currentFile?.filePath || ''}
            />
          </FormGroup>
          {closeTabButton}
        </div>
        <FormGroup
          inline
          label={
            <H6>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .form.name
              }
            </H6>
          }
          labelFor="sd-project-file-upload-panel-name"
        >
          <InputGroup
            fill
            id="sd-project-file-upload-panel-name"
            name="name"
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              setFileName(evt.target.value);
            }}
            value={currentFile?.fileName || ''}
          />
        </FormGroup>
        <ProjectFileUploadTable
          fields={fields}
          onFieldLabelChange={setFields}
          rows={visibleRows}
        />
      </>
    );
  } else if (currentFile?.filePath || '') {
    preview = (
      <Callout
        className={classNames({
          'sd-project-file-upload-panel__preview__error-with-close':
            files.length > 1,
        })}
        intent={Intent.WARNING}
      >
        <H4>
          <Text>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel
                .errors.empty
            }
          </Text>
          {closeTabButton}
        </H4>
      </Callout>
    );
  }

  return (
    <div className="sd-project-file-upload-panel">
      <div className="sd-project-file-upload-panel__scroll-wrapper">
        <div
          className={classNames(
            'sd-project-file-upload-panel__resource-properties',
            Classes.CALLOUT
          )}
        >
          <H5>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel.form
                .resourceProperties
            }
          </H5>
          <ProjectFileUploadOptionsForm
            disableHeaderToggle={isThereDuplicateHeaders}
            fileOptions={fileOptions}
            onSetFileOptions={setFileOptions}
          />
        </div>
        <div
          className={classNames(
            'sd-project-file-upload-panel__preview',
            Classes.CALLOUT
          )}
        >
          <div className="sd-project-file-upload-panel__preview__header">
            <H5>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .form.preview
              }
            </H5>
            <div data-testid={TEST_IDS.projectFileUploadPanel.rowCount}>
              {visibleRows.length > 0
                ? copy.components.projectResourceDialog.projectFileUploadPanel.form.previewRowCount(
                    visibleRows.length,
                    !currentFile?.isFilePartial &&
                      visibleRows.length === rowsByFileIdx[selectedTab]?.length
                  )
                : ''}
            </div>
          </div>
          {files.length < 2 ? (
            <>{preview}</>
          ) : (
            <Tabs
              animate={false}
              onChange={(tabIdx: number) => {
                setSelectedTab(tabIdx);
              }}
              renderActiveTabPanelOnly
              selectedTabId={selectedTab}
            >
              {files.map((file, idx) => (
                <Tab
                  key={file.id}
                  // while filePath is not always unique,
                  // this wont be an issue for testid (unlike key)
                  className="sd-project-file-upload-panel__preview__tab"
                  data-testid={getProjectFileUploadPanelPreviewTabTestId(
                    file.filePath
                  )}
                  id={idx}
                  panel={
                    <>
                      <Divider className="sd-project-file-upload-panel__preview__tab-divider" />
                      {preview}
                    </>
                  }
                >
                  <Text ellipsize>
                    {file.fileName ||
                      copy.components.projectResourceDialog.projectFileUploadPanel.form.namelessTabLabel(
                        idx + 1
                      )}
                  </Text>
                  {parserErrorsByFileIdx[idx]?.length ||
                  !rowsByFileIdx[idx]?.length ||
                  !file.fileName ? (
                    <Icon icon={IconNames.WARNING_SIGN} />
                  ) : null}
                </Tab>
              ))}
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
};
